import { useState, useRef, useEffect, useMemo } from 'react';
import { Heart, FileText, Play, Music, Image, Link2, File, ChevronRight } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import { NowPlayingIndicator, TrackActionMenu } from '@/components/audio';
import { useAudio } from '@/context/AudioContext';
import type { FavoriteItem } from '@/types/favorites';
import type { FileType } from '@/types/library';
import type { AudioTrack } from '@/types/audio';

const FILE_TYPE_ICONS: Record<string, typeof FileText> = {
  pdf: FileText,
  doc: FileText,
  video: Play,
  audio: Music,
  image: Image,
  link: Link2,
  other: File,
};

const FILE_TYPE_COLORS: Record<string, string> = {
  pdf: 'text-red-400',
  doc: 'text-[#8e4585]',
  video: 'text-[#8e4585]',
  audio: 'text-green-400',
  image: 'text-blue-400',
  link: 'text-cyan-400',
  other: 'text-slate-400',
};

interface FileFavoritesDropdownProps {
  onOpenFile?: (favorite: FavoriteItem) => void;
}

export function FileFavoritesDropdown({ onOpenFile }: FileFavoritesDropdownProps) {
  const { getFilteredFavorites } = useFavorites();
  const { currentTrack, isPlaying, pause, resume } = useAudio();
  const [isOpen, setIsOpen] = useState(false);
  const [audioMenuTrack, setAudioMenuTrack] = useState<AudioTrack | null>(null);
  const [showAudioMenu, setShowAudioMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fileFavorites = useMemo(() => {
    return getFilteredFavorites('file');
  }, [getFilteredFavorites]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleItemClick = (favorite: FavoriteItem) => {
    const fileType = favorite.cachedData.fileType;

    if (fileType === 'audio') {
      const isThisTrackLoaded = currentTrack?.id === favorite.itemId;
      if (isThisTrackLoaded) {
        if (isPlaying) pause();
        else resume();
      } else {
        const track: AudioTrack = {
          id: favorite.itemId,
          url: favorite.cachedData.url || '',
          title: favorite.cachedData.title || 'Audio',
          fileName: favorite.cachedData.fileName,
          mimeType: favorite.cachedData.mimeType,
          thumbnailUrl: favorite.cachedData.thumbnailUrl,
          source: 'library',
        };
        setAudioMenuTrack(track);
        setShowAudioMenu(true);
      }
      setIsOpen(false);
      return;
    }

    onOpenFile?.(favorite);
    setIsOpen(false);
  };

  const totalCount = fileFavorites.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-lg transition-colors relative ${
          isOpen ? 'bg-[#8e4585]' : 'hover:bg-[#8e4585]'
        }`}
        aria-label="Favorite materials"
        aria-expanded={isOpen}
      >
        <Heart size={20} className={totalCount > 0 ? 'fill-current' : ''} />

        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-amber-400 text-amber-900 text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[110]" onClick={() => setIsOpen(false)} />

          <div className="fixed left-1/2 -translate-x-1/2 top-[52px] w-[calc(100%-1.5rem)] max-w-sm bg-white rounded-xl shadow-xl border border-[#e5bce5]/40 overflow-hidden z-[120] max-h-[55vh] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-[#f0d2f0]/50 px-4 py-2.5 flex-shrink-0">
              <h3 className="font-semibold text-[#5a3d58] text-sm">Favorite Materials</h3>
              <p className="text-[10px] text-[#8e4585]/60">
                {totalCount} {totalCount === 1 ? 'item' : 'items'} saved
              </p>
            </div>

            {totalCount === 0 ? (
              <div className="p-6 text-center">
                <Heart size={28} className="mx-auto mb-2 text-[#e5bce5]" />
                <p className="text-[#5a3d58]/60 text-sm">No favorite materials yet</p>
                <p className="text-[#8e4585]/40 text-xs mt-1">
                  Tap the heart on any material to save it
                </p>
              </div>
            ) : (
              <div className="py-1 overflow-y-auto flex-1">
                {fileFavorites.map((fav) => {
                  const fileType = (fav.cachedData.fileType as FileType) || 'other';
                  const Icon = FILE_TYPE_ICONS[fileType] || File;
                  const iconColor = FILE_TYPE_COLORS[fileType] || 'text-slate-400';
                  const isAudio = fileType === 'audio';
                  const isThisPlaying = isAudio && currentTrack?.id === fav.itemId && isPlaying;

                  return (
                    <button
                      key={fav.id}
                      onClick={() => handleItemClick(fav)}
                      className={`w-full px-3 py-2.5 text-left hover:bg-[#faf5fa] active:bg-[#f8e8f8] transition-colors flex items-center gap-3 group ${
                        isThisPlaying ? 'bg-[#f8e8f8]' : ''
                      }`}
                    >
                      {fav.cachedData.thumbnailUrl ? (
                        <img
                          src={fav.cachedData.thumbnailUrl}
                          alt=""
                          className="w-9 h-9 rounded-lg object-cover shrink-0 bg-[#faf8f5]"
                        />
                      ) : (
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#fcf4fc]`}
                        >
                          {isThisPlaying ? (
                            <NowPlayingIndicator size="sm" />
                          ) : (
                            <Icon size={16} className={iconColor} />
                          )}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="text-[#5a3d58] text-sm font-medium truncate">
                          {fav.cachedData.title || 'Untitled'}
                        </p>
                        {fav.cachedData.fileName &&
                          fav.cachedData.fileName !== fav.cachedData.title && (
                            <p className="text-[#8e4585]/50 text-[10px] truncate">
                              {fav.cachedData.fileName}
                            </p>
                          )}
                      </div>

                      <ChevronRight
                        size={14}
                        className="text-[#e5bce5] group-hover:text-[#8e4585] transition-colors shrink-0"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {audioMenuTrack && (
        <TrackActionMenu
          track={audioMenuTrack}
          isOpen={showAudioMenu}
          onClose={() => {
            setShowAudioMenu(false);
            setAudioMenuTrack(null);
          }}
        />
      )}
    </div>
  );
}
